import React from 'react';
import { motion } from 'framer-motion';
import { Icon, Button, Badge } from '../ui';

const packs = [
  {
    id: 'pme',
    title: 'PME',
    icon: 'IconBuilding',
    monthly: 29,
    yearly: 290,
    desc: 'Pour les petites et moyennes entreprises',
    popular: false,
    features: [
      'Facturation électronique illimitée',
      'Module TEJ & Fiscalité',
      'Trésorerie & Soldes',
      'Déclarations TVA',
      'Support prioritaire',
    ],
  },
  {
    id: 'cabinets',
    title: 'Cabinets',
    icon: 'IconBuildingSkyscraper',
    monthly: 49,
    yearly: 490,
    desc: 'Pour les experts-comptables et cabinets',
    popular: true,
    features: [
      'Tous les services PME',
      'Gestion de cabinets',
      'Gestion des tâches & Kanban',
      'Collaborateurs & Timesheet',
      'Gestion multidossier clients',
    ],
  },
];

export default function PricingSection({ onSignup }) {
  const [yearly, setYearly] = React.useState(false);

  return (
    <section id="pricing" className="mb-32">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-12"
      >
        <h2 className="text-4xl font-bold text-white mb-4">Nos offres</h2>
        <p className="text-slate-400 text-lg max-w-2xl mx-auto mb-8">Choisissez le pack adapté à votre structure</p>

        <div className="inline-flex items-center bg-slate-800/50 border border-slate-700/50 rounded-2xl p-1.5">
          <button
            onClick={() => setYearly(false)}
            className={`px-6 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${!yearly ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
          >Mensuel</button>
          <button
            onClick={() => setYearly(true)}
            className={`px-6 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${yearly ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
          >
            Annuel
            <span className="ml-1.5 text-xs text-emerald-400 font-bold">-2 mois offerts</span>
          </button>
        </div>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {packs.map((pack, i) => {
          const price = yearly ? pack.yearly : pack.monthly;
          const suffix = yearly ? 'DT/an' : 'DT/mois';
          return (
            <motion.div
              key={pack.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              className={`relative bg-gradient-to-br from-slate-900/60 to-slate-800/30 backdrop-blur-xl border rounded-2xl p-8 transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl ${
                pack.popular ? 'border-indigo-500/50 shadow-xl shadow-indigo-500/10 scale-105 md:scale-105 z-10' : 'border-slate-700/50 hover:border-indigo-500/30'
              }`}
            >
              {pack.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge variant="info" className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-4 py-1 text-xs font-bold whitespace-nowrap">LE PLUS CHOISI</Badge>
                </div>
              )}
              <div className="w-14 h-14 bg-gradient-to-br from-indigo-500/20 to-blue-500/20 rounded-2xl flex items-center justify-center mb-4 border border-indigo-500/30">
                <Icon name={pack.icon} size={28} className="text-indigo-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-1">{pack.title}</h3>
              <p className="text-slate-400 text-sm mb-6">{pack.desc}</p>

              <div className="mb-8">
                <span className="text-5xl font-black text-white">{price}</span>
                <span className="text-slate-400 ml-2">{suffix}</span>
                <div className="text-xs text-slate-500 mt-1">HTVA</div>
                {yearly && (
                  <div className="text-xs text-emerald-400 mt-1 font-medium">Économisez {pack.monthly * 12 - pack.yearly} DT / an</div>
                )}
              </div>

              <ul className="space-y-3 mb-8">
                {pack.features.map((f, idx) => (
                  <li key={idx} className={`flex items-center gap-3 text-sm ${idx >= (pack.id === 'cabinets' ? 1 : 0) && pack.id === 'cabinets' && idx === 1 ? 'border-t border-slate-700/50 pt-3 mt-3' : ''}`}>
                    <Icon name="IconCheck" size={16} className="text-emerald-400 flex-shrink-0" />
                    <span className={idx >= (pack.features.length - (pack.id === 'cabinets' ? 4 : 0)) && pack.id === 'cabinets' ? 'text-white font-medium' : 'text-slate-300'}>{f}</span>
                  </li>
                ))}
              </ul>

              <Button onClick={onSignup} variant={pack.popular ? 'primary' : 'ghost'} className={`w-full justify-center py-3 ${pack.popular ? '' : 'border-slate-600 text-slate-300 hover:bg-slate-800'}`}>
                Essai gratuit
              </Button>
            </motion.div>
          );
        })}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mt-12 max-w-2xl mx-auto bg-gradient-to-br from-slate-900/40 to-indigo-900/10 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8"
      >
        <Icon name="IconHandshake" size={36} className="mx-auto mb-4 text-indigo-400" />
        <h3 className="text-xl font-bold text-white mb-2">Partenariat Win-Win</h3>
        <p className="text-slate-400 text-sm leading-relaxed">
          Vous êtes un cabinet et vous souhaitez revendre la solution à vos clients ?
          Contactez-nous pour un partenariat avantageux avec des conditions préférentielles.
        </p>
        <a href="mailto:partenariat@accountintime.tn" className="inline-flex items-center gap-2 mt-4 px-6 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-medium rounded-xl hover:scale-105 transition-all duration-300">
          <Icon name="IconMail" size={18} />
          Nous contacter
        </a>
      </motion.div>
    </section>
  );
}
